import type {
    AnalyticsButtonInfo,
    TicketData,
    TicketTimeData,
} from '@beat-venues/common-types';
import { urlUtil } from '@beat-venues/utils';
import { useTranslation } from 'next-i18next';
import { useState, type JSX } from 'react';

import { Button, Heading, LogoTag, StatusLabel, Text } from '../../atoms';
import type { CommonProps } from '../../types';
import { useAnalytics } from '../../useAnalytics';
import { ReminderButton } from '../reminder-button';
import { EventSeriesSensoryIcons } from '../event-series-sensory-icons';
import { tv } from 'tailwind-variants';

export interface BeatTicketsItemProps
    extends CommonProps,
        Omit<TicketData, 'organizerName' | 'organizerNameTranslated' | 'id'> {
    onBuyTicketClick?: (value: AnalyticsButtonInfo) => void;
    eventTitle: string;
    locale: string;
}

export const BeatTicketsItemStyles = tv({
    slots: {
        root: 'lg:flex items-center justify-between w-full bg-gray-900 rounded-xl lg:rounded-full p-5 lg:px-12 lg:py-8',
        buttonContainer:
            'mt-1 flex flex-col gap-2 lg:mt-0 lg:flex-row-reverse lg:gap-4 lg:order-2',
    },
});

export const BeatTicketsItem = ({
    className,
    date,
    times,
    dateFull,
    venueId,
    venueName,
    eventTitle,
    onBuyTicketClick,
    locale,
    testId = 'beat-tickets-item',
    ...props
}: BeatTicketsItemProps): JSX.Element => {
    const dateArray = date && date.split(' ');

    const { t } = useTranslation(['global', 'event']);
    const currentLocale =
        locale ?? process.env['NEXT_PUBLIC_DEFAULT_LOCALE'] ?? 'nl';
    const camefromParam = useAnalytics();
    const [modalIsShown, setModalIsShown] = useState(false);

    const subStringDepth = locale === 'fr' ? 4 : locale === 'en' ? 3 : 2;

    const { root, buttonContainer } = BeatTicketsItemStyles();

    const renderButtonContainer = (size: 'sm' | 'md', time: TicketTimeData) => {
        const displayClass = size === 'sm' ? 'lg:hidden' : 'hidden lg:flex';
        return (
            <div className={buttonContainer({ className: displayClass })}>
                <div className="flex">
                    <div className="relative">
                        {time.cta && (
                            <Button
                                className="relative z-30"
                                disabled={time.cta.isDisabled}
                                href={
                                    time.cta.url &&
                                    urlUtil.buildBuyButtonUrl(
                                        time.cta.url,
                                        camefromParam
                                    )
                                }
                                id={`buy-tickets-event-${time.id}`}
                                intent="secondary"
                                size={size}
                                onClick={() => {
                                    const buttonInfo: AnalyticsButtonInfo = {
                                        event: 'button_click',
                                        id: `buy-tickets-event-${time.id}`,
                                        name: 'buy tickets',
                                        text: t(
                                            `ctaStatusLabels.${time?.cta?.label}`,
                                            {
                                                ns: 'event',
                                            }
                                        ),
                                        category: 'event ticket link',
                                        type: 'cta',
                                        target_url: time.cta?.url || '',
                                    };
                                    if (onBuyTicketClick) {
                                        onBuyTicketClick(buttonInfo);
                                    }
                                }}
                            >
                                {/\d/.test(time.cta.label)
                                    ? time.cta.label
                                    : t(`ctaStatusLabels.${time.cta.label}`, {
                                          ns: 'event',
                                      })}
                            </Button>
                        )}
                        {time.reminder && (
                            <div className="absolute left-1/2 top-1/2 z-20 box-content h-full w-full -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-gray-900 bg-gray-900"></div>
                        )}
                    </div>

                    {time.reminder && (
                        <ReminderButton
                            eventData={{
                                title: eventTitle,
                                label: t(`reminder.cta.setReminder`, {
                                    ns: 'event',
                                }),
                                icalLabel: t(`reminder.label.ical`, {
                                    ns: 'event',
                                }),
                            }}
                            id={`set-reminder-event-${time.id}`}
                            onClick={() => {
                                const buttonInfo: AnalyticsButtonInfo = {
                                    event: 'button_click',
                                    id: `${time.id}`,
                                    name: 'set reminder',
                                    text: t(`reminder.cta.setReminder`, {
                                        ns: 'event',
                                    }),
                                    category: 'event reminder cta',
                                    type: 'cta',
                                    target_url: '',
                                };
                                if (onBuyTicketClick) {
                                    onBuyTicketClick(buttonInfo);
                                }
                            }}
                            location={venueName[currentLocale]}
                            modalIsShown={modalIsShown}
                            reminderDate={time.reminder.originalDate}
                            setModalIsShown={setModalIsShown}
                            variant="small"
                        />
                    )}
                </div>
                {time.tags && time.tags.length > 0 && (
                    <EventSeriesSensoryIcons tags={time.tags} />
                )}
            </div>
        );
    };

    return (
        <>
            {times.map((time) => (
                <li
                    key={time.id}
                    className={root({ className })}
                    {...props}
                    data-testid={testId}
                >
                    <div className="items-center gap-8 lg:flex">
                        <div className="flex items-center lg:block">
                            <LogoTag
                                className="hidden lg:flex"
                                size="sm"
                                venueId={venueId}
                            />
                            <Text className="uppercase lg:hidden" size="md">
                                {dateFull} {time.time}
                            </Text>
                            {time.status && (
                                <StatusLabel className="ml-2 lg:hidden">
                                    {time.status}
                                </StatusLabel>
                            )}
                        </div>

                        {date && (
                            <div className="hidden gap-1 uppercase lg:flex">
                                <Text className="leading-0 mr-2 text-6xl">
                                    {dateArray[0]}
                                </Text>
                                <Text className="leading-0" size="xl">
                                    {dateArray[1]}
                                    <br />
                                    {dateArray[2]}
                                </Text>
                            </div>
                        )}

                        <div>
                            <div className="hidden items-center lg:flex">
                                {dateFull && time.time && (
                                    <Text className="mr-2" size="xl">
                                        {dateFull.substring(0, subStringDepth)}{' '}
                                        {time.time}
                                    </Text>
                                )}

                                {time.status && (
                                    <StatusLabel>{time.status}</StatusLabel>
                                )}
                            </div>
                            <Heading as="h3" className="mb-0 font-light">
                                {venueId !== 'beat-tickets'
                                    ? t(`venues.${venueId}.name`, {
                                          ns: 'global',
                                      })
                                    : venueName[currentLocale]}
                            </Heading>
                            {time.name && (
                                <Heading
                                    as="h4"
                                    className="mb-0 font-light"
                                    size="h5"
                                >
                                    {time.name}
                                </Heading>
                            )}
                        </div>
                    </div>

                    <div className="flex gap-2 max-lg:flex-col lg:gap-4">
                        {renderButtonContainer('sm', time)}
                        {renderButtonContainer('md', time)}
                    </div>
                </li>
            ))}
        </>
    );
};
